import { cond, equals, groupBy, head, isNil, join, last, map, pipe, prop, props, split, T, toPairs } from 'ramda';
import React, { FC, ReactNode, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Text } from '../../../components/common';
import { ISectionData } from '../../../components/layouts/Sections/Section/Section.types';
import { MediaType } from '../../../constants/MediaType';
import { MainSource } from '../../../constants/Source';
import { encodeFolderId } from '../../../utils/hoc/extended-sources/withShutterstock.utils';
import { encodePublicMediaFolderId } from '../../../utils/hoc/withPublicMedia/utils';
import { ICategoriesWithItems } from '../../../utils/hoc/withPublicMedia/withPublicMedia.types';
import { MediaManagerSDKAnyItemWithSectionInfo } from '../../../utils/media-manager-sdk';
import { RouteBuilder } from '../../../utils/RouteHelpers';

const Title: FC<{ sourceNameTranslationId: string; subCategoryTitle?: ReactNode }> = ({
  sourceNameTranslationId,
  subCategoryTitle,
}) => {
  const categoryTitle = subCategoryTitle || <FormattedMessage id="media-type.images" />;

  return (
    <FormattedMessage
      id="search.results-from-new-search"
      values={{
        categoryName: (
          <Text weight="bold" size="medium">
            {categoryTitle}
          </Text>
        ),
        resultsFrom: (
          <Text weight="bold" size="medium">
            <FormattedMessage id={sourceNameTranslationId} />
          </Text>
        ),
      }}
    />
  );
};

const getSectionConfig = (source, subCategoryTitle, folderId) =>
  ({
    [MainSource.SiteFiles]: {
      title: <Title sourceNameTranslationId="categories.site-files" subCategoryTitle={subCategoryTitle} />,
      action: (searchQuery: string) => ({
        url: RouteBuilder.Search(MainSource.SiteFiles, searchQuery),
        title: <FormattedMessage id="search.show-all-results" />,
      }),
    },
    [MainSource.Trash]: {
      title: <Title sourceNameTranslationId="categories.trash" />,
      action: (searchQuery: string) => ({
        url: RouteBuilder.Search(MainSource.Trash, searchQuery),
        title: <FormattedMessage id="search.show-all-results" />,
      }),
    },
    [MainSource.MediaFromWix]: {
      title: <Title sourceNameTranslationId="categories.free-from-wix" subCategoryTitle={subCategoryTitle} />,
      action: (searchQuery: string) => ({
        url: RouteBuilder.Search(MainSource.MediaFromWix, searchQuery, folderId),
        title: <FormattedMessage id="search.show-all-results" />,
      }),
    },
    [MainSource.NTT]: {
      title: <Title sourceNameTranslationId="categories.ntt" />,
      action: (searchQuery: string) => ({
        url: RouteBuilder.Search(MainSource.NTT, searchQuery),
        title: <FormattedMessage id="search.show-all-results" />,
      }),
    },
    [MainSource.Yell]: {
      title: <Title sourceNameTranslationId="categories.yell" />,
      action: (searchQuery: string) => ({
        url: RouteBuilder.Search(MainSource.Yell, searchQuery),
        title: <FormattedMessage id="search.show-all-results" />,
      }),
    },
    [MainSource.Shutterstock]: {
      title: <Title sourceNameTranslationId="categories.shutterstock" subCategoryTitle={subCategoryTitle} />,
      action: (searchQuery: string) => ({
        url: RouteBuilder.Search(MainSource.Shutterstock, searchQuery, folderId),
        title: <FormattedMessage id="search.show-all-results" />,
      }),
    },
    [MainSource.Unsplash]: {
      title: <Title sourceNameTranslationId="categories.unsplash" />,
      action: (searchQuery: string) => ({
        url: RouteBuilder.Search(MainSource.Unsplash, searchQuery),
        title: <FormattedMessage id="search.show-all-results" />,
      }),
    },
    [MainSource.VisitorsUploads]: {
      title: <Title sourceNameTranslationId="categories.visitor-uploads" />,
      action: (searchQuery: string) => ({
        url: RouteBuilder.Search(MainSource.VisitorsUploads, searchQuery),
        title: <FormattedMessage id="search.show-all-results" />,
      }),
    },
  }[source]);

const getFolderId = (source, mediaType, firstItem) =>
  (source === MainSource.MediaFromWix &&
    encodePublicMediaFolderId({
      categoryId: firstItem.parentCategoryId,
      subCategoryId: firstItem.categoryId,
    })) ||
  (source === MainSource.Shutterstock && encodeFolderId({ mediaType })) ||
  firstItem.categoryId;

const getSubCategoryTitle = (source, mediaType, firstItem) =>
  (source === MainSource.Shutterstock && (
    <FormattedMessage id={mediaType === MediaType.Video ? 'media-type.videos' : 'media-type.images'} />
  )) ||
  (source === MainSource.SiteFiles && <FormattedMessage id="media-type.site-files" />) ||
  firstItem.subCategoryTitle;

export const useSections = ({
  items,
  searchQuery,
}: {
  items: MediaManagerSDKAnyItemWithSectionInfo[];
  searchQuery: string;
}) =>
  useMemo<ISectionData[]>(
    () =>
      pipe<
        MediaManagerSDKAnyItemWithSectionInfo[],
        ICategoriesWithItems,
        [string, MediaManagerSDKAnyItemWithSectionInfo[]][],
        ISectionData[]
      >(
        groupBy<MediaManagerSDKAnyItemWithSectionInfo>(
          cond([
            [pipe(prop('source'), equals(MainSource.Shutterstock)), pipe(props(['source', 'mediaType']), join('.'))],
            [pipe(prop('categoryId'), isNil), prop('source')],
            [T, pipe(props(['source', 'categoryId']), join('.'))],
          ]),
        ),
        toPairs,
        map(([mixedSource, items]) => {
          const source = head(split('.', mixedSource));
          const mediaType = last(split('.', mixedSource)) as MediaType;

          const firstItem = head(items);
          const folderId = getFolderId(source, mediaType, firstItem);
          const subCategoryTitle = getSubCategoryTitle(source, mediaType, firstItem);

          const sectionConfig = getSectionConfig(source, subCategoryTitle, folderId);

          return {
            title: sectionConfig.title,
            subCategoryId: firstItem.categoryId,
            sectionName: source,
            data: items,
            action: sectionConfig.action(searchQuery),
          };
        }),
      )(items) as ISectionData[],
    [items, searchQuery],
  );
